import React, { useState, useRef } from 'react';
import {
    Button, Typography, Box, Alert, IconButton, Collapse, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, Tooltip
} from "@mui/material";
import {
    Download as DownloadIcon, Upload as UploadIcon,
    Clear as ClearIcon, CloudUpload as CloudUploadIcon,
    ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
    Warning as WarningIcon, Visibility as VisibilityIcon,
    ChevronLeft as CollapseIcon, ChevronRight as ExpandIcon,
    Collections as CollectionsIcon
} from '@mui/icons-material';
import EndonymCard from './EndonymCard';
import SeenPlaceCard from './SeenPlaceCard';

const EndonymsList = ({
    savedPlaces,
    onDownloadEndonyms,
    onUploadEndonyms,
    onClearEndonyms,
    onUploadToWikidata,
    onUpdateLabel,
    onDeleteLabel,
    onMapFocus,
    expanded,
    onExpandedChange
}) => {
    const [seenExpanded, setSeenExpanded] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [showClearDialog, setShowClearDialog] = useState(false);
    const fileInputRef = useRef(null);
    const allSavedEntries = Array.from(savedPlaces).map(savedPlace => JSON.parse(savedPlace));
    // Filter out "seen" entries for the endonyms list (they don't have actual labels)
    const addedLabels = allSavedEntries.filter(entry => !entry.isSeen && entry.label && entry.label.trim());
    // Count "seen" entries separately
    const seenPlaces = allSavedEntries.filter(entry => entry.isSeen);
    const hasLabels = addedLabels.length > 0;
    
    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        // Reset error state
        setUploadError('');

        // Check file type
        if (!file.name.endsWith('.json')) {
            setUploadError('Please select a JSON file (.json)');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const jsonContent = e.target.result;
                const data = JSON.parse(jsonContent);
                
                let endonyms;
                
                // Validate JSON structure - handle both legacy and new formats
                if (Array.isArray(data)) {
                    // Legacy format - direct array of endonyms
                    endonyms = data;
                } else if (data.endonyms && Array.isArray(data.endonyms)) {
                    // New format with metadata
                    endonyms = data.endonyms;
                } else {
                    setUploadError('JSON file must contain either an array of endonyms or an object with "endonyms" property containing an array');
                    return;
                }

                // Validate each endonym object
                for (let i = 0; i < endonyms.length; i++) {
                    const item = endonyms[i];
                    if (!item.label || !item.place || !item.language) {
                        setUploadError(`Invalid endonym structure at index ${i}. Each endonym must have 'label', 'place', and 'language' properties.`);
                        return;
                    }
                    
                    // Validate place object
                    if (!item.place.wikidataID || !item.place.lat || !item.place.lon) {
                        setUploadError(`Invalid place data at index ${i}. Place must have 'wikidataID', 'lat', and 'lon' properties.`);
                        return;
                    }
                }

                // If validation passes, call the upload handler
                onUploadEndonyms(data);
                
            } catch (error) {
                setUploadError('Invalid JSON file format. Please check your file and try again.');
                console.error('JSON parsing error:', error);
            }
        };

        reader.onerror = () => {
            setUploadError('Error reading file. Please try again.');
        };

        reader.readAsText(file);
        
        // Reset file input
        event.target.value = '';
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleClearClick = () => {
        setShowClearDialog(true);
    };

    const handleConfirmClear = () => {
        onClearEndonyms();
        setShowClearDialog(false);
    };

    const handleCancelClear = () => {
        setShowClearDialog(false);
    };

    return (
        <Paper
            elevation={8}
            sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                transition: 'all 0.3s ease-in-out',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            {/* Modern Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    borderBottom: expanded ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white'
                }}
            >
                {expanded && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <CollectionsIcon />
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Added Endonyms ({addedLabels.length})
                            </Typography>
                            {seenPlaces.length > 0 && (
                                <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', opacity: 0.9 }}>
                                    {seenPlaces.length} place{seenPlaces.length !== 1 ? 's' : ''} marked as seen
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
                
                <IconButton
                    onClick={() => onExpandedChange(!expanded)}
                    sx={{
                        color: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)'
                        }
                    }}
                    size="small"
                >
                    {expanded ? <CollapseIcon /> : <ExpandIcon />}
                </IconButton>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={expanded} timeout={300}>
                <Box sx={{ 
                    p: 2, 
                    display: 'flex', 
                    flexDirection: 'column',
                    maxHeight: 'calc(100vh - 250px)',
                    overflow: 'auto'
                }}>
                    {/* Upload Error Alert */}
                    {uploadError && (
                        <Alert 
                            severity="error" 
                            onClose={() => setUploadError('')}
                            sx={{ mb: 2 }}
                        >
                            <Typography variant="body2">
                                <strong>Upload Error:</strong> {uploadError}
                            </Typography>
                        </Alert>
                    )}

                    {hasLabels && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                            Click any endonym to edit it
                        </Typography>
                    )}

                    {/* Endonym Cards */}
                    {hasLabels ? (
                        <Box sx={{ mb: 2, flex: '1 1 auto', overflow: 'auto' }}>
                            {addedLabels.map((addedLabel, index) => (
                                <EndonymCard
                                    key={`${addedLabel.place.wikidataID}-${index}`}
                                    addedLabel={addedLabel}
                                    onUpdate={onUpdateLabel}
                                    onDelete={onDeleteLabel}
                                    onMapFocus={onMapFocus}
                                />
                            ))}
                        </Box>
                    ) : (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body1">
                                No endonyms added yet. Click on map markers to add place names in your language.
                            </Typography>
                        </Alert>
                    )}

                    {/* Seen Places Section */}
                    {seenPlaces.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            {/* Seen Places Header */}
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                p: 1.5,
                                borderBottom: seenExpanded ? '1px solid rgba(226, 232, 240, 0.5)' : 'none',
                                cursor: 'pointer',
                                borderRadius: seenExpanded ? '12px 12px 0 0' : '12px',
                                backgroundColor: 'rgba(156, 163, 175, 0.1)',
                                border: '1px solid rgba(156, 163, 175, 0.3)',
                                '&:hover': {
                                    backgroundColor: 'rgba(156, 163, 175, 0.15)'
                                },
                                transition: 'all 0.2s ease-in-out'
                            }}
                            onClick={() => setSeenExpanded(!seenExpanded)}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <VisibilityIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                        Seen Places ({seenPlaces.length})
                                    </Typography>
                                </Box>
                                <IconButton 
                                    size="small"
                                    sx={{ 
                                        transform: seenExpanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                                        transition: 'transform 0.3s ease-in-out',
                                        color: 'text.secondary'
                                    }}
                                >
                                    {seenExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            </Box>

                            {/* Seen Places Content */}
                            <Collapse in={seenExpanded} timeout={300}>
                                <Box sx={{ 
                                    p: 2, 
                                    backgroundColor: 'rgba(156, 163, 175, 0.05)',
                                    border: '1px solid rgba(156, 163, 175, 0.3)',
                                    borderTop: 'none',
                                    borderRadius: '0 0 12px 12px',
                                    maxHeight: '300px',
                                    overflow: 'auto'
                                }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                                        Places you've marked as seen after editing directly on Wikidata
                                    </Typography>
                                    
                                    {seenPlaces.map((seenEntry, index) => (
                                        <SeenPlaceCard
                                            key={`seen-${seenEntry.place.wikidataID}-${index}`}
                                            seenEntry={seenEntry}
                                            onDelete={onDeleteLabel}
                                            onMapFocus={onMapFocus}
                                        />
                                    ))}
                                </Box>
                            </Collapse>
                        </Box>
                    )}

                    {/* Hidden File Input */}
                    <input
                        id="upload-input"
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        accept=".json"
                        style={{ display: 'none' }}
                    />

                    {/* Action Buttons */}
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        justifyContent: 'flex-end',
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        flex: '0 0 auto'
                    }}>
                        {/* upload to WikiData - only available in development due to CORS/OAuth limitations */}
                        {process.env.NODE_ENV !== "production" && hasLabels && (
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<CloudUploadIcon />}
                                onClick={onUploadToWikidata}
                                size="small"
                            >
                                To Wikidata
                            </Button>
                        )}
                        
                        {/* Production info for Wikidata upload */}
                        {process.env.NODE_ENV === "production" && hasLabels && (
                            <Alert 
                                severity="info" 
                                sx={{ 
                                    fontSize: '0.75rem',
                                    borderRadius: 2,
                                    mb: 1,
                                    p: 1
                                }}
                            >
                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 500, mb: 0.5 }}>
                                    Want to upload to Wikidata?
                                </Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                                    Run locally for full upload functionality: <code style={{ fontSize: '0.65rem' }}>git clone</code> then <code style={{ fontSize: '0.65rem' }}>npm start</code>
                                </Typography>
                            </Alert>
                        )}

                        {/* Upload JSON file button*/}
                        <Tooltip
                            title="Load previously saved endonyms from a JSON file. This will add them to your current list."
                            arrow
                            placement="top"
                        >
                            <Button
                                variant="outlined"
                                startIcon={<UploadIcon />}
                                onClick={handleUploadClick}
                                size="small"
                                sx={{
                                    '&:hover': {
                                        backgroundColor: 'rgba(16, 185, 129, 0.1)',
                                        borderColor: 'success.main'
                                    }
                                }}
                            >
                                Upload JSON
                            </Button>
                        </Tooltip>

                        {/* clear JSON content button*/}
                        <Tooltip
                            title="Remove all endonyms from your list. Consider downloading first as this action cannot be undone."
                            arrow
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearClick}
                                    disabled={!hasLabels}
                                    size="small"
                                >
                                    Clear
                                </Button>
                            </span>
                        </Tooltip>

                        {/* Download JSON file button*/}
                        <Tooltip
                            title="Save all your endonyms to a JSON file. Perfect for backup, sharing, or importing into research tools."
                            arrow
                            placement="top"
                        >
                            <span>
                                <Button
                                    variant="contained"
                                    startIcon={<DownloadIcon />}
                                    onClick={onDownloadEndonyms}
                                    disabled={!hasLabels}
                                    size="small"
                                    sx={{
                                        backgroundColor: 'success.main',
                                        '&:hover': {
                                            backgroundColor: 'success.dark'
                                        }
                                    }}
                                >
                                    Download JSON
                                </Button>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
            </Collapse>

            {/* Collapsed State Indicator */}
            {!expanded && (
                <Box
                    sx={{
                        p: 1.5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <IconButton
                        color={addedLabels.length > 0 ? 'success' : 'primary'}
                        size="small"
                    >
                        <CollectionsIcon />
                    </IconButton>
                    {addedLabels.length > 0 && (
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                            {addedLabels.length}
                        </Typography>
                    )}
                </Box>
            )}

            {/* Clear Confirmation Dialog */}
            <Dialog
                open={showClearDialog}
                onClose={handleCancelClear}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    color: 'warning.main'
                }}>
                    <WarningIcon />
                    <Typography variant="h6" component="div">
                        Clear All Endonyms?
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Are you sure you want to clear all <strong>{addedLabels.length} endonyms</strong>? 
                        This action cannot be undone.
                    </DialogContentText>
                    <DialogContentText sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
                        💡 <strong>Tip:</strong> Consider downloading your endonyms as a JSON file before clearing, 
                        so you can restore them later if needed.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 3, gap: 1 }}>
                    <Button 
                        onClick={handleCancelClear}
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleConfirmClear}
                        variant="contained"
                        color="warning"
                        startIcon={<ClearIcon />}
                        sx={{ borderRadius: 2, px: 3 }}
                    >
                        Clear All ({addedLabels.length})
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default EndonymsList;

// Helper function to focus map on a specific place
export const focusMapOnPlace = (mapRef, place, zoomLevel = 16) => {
    if (mapRef.current) {
        mapRef.current.setView([place.lat, place.lon], zoomLevel);
    }
};